import { Button, Card, InputField, LinkButton } from '@pmi/dsm-react';
import React, { useEffect, useState } from 'react';
import styles from './accountSupportContact.module.scss';
import {
  normalizeEmail,
  pmiEmailRegex,
  singleEmailRegex,
} from '../../../shared/helpers';
import { useGetAccountDataQuery } from '../../../shared/navigation/apiSlice';
import { useUpdateAccountSupportEmailMutation } from '../AgreementSettings/apiSlice';
import { toastWithAnalytics } from '../../../shared/analytics';
import usePermissions from '../../../shared/hooks/usePermissions';
import PERMISSIONS from '../../../shared/permissions';

interface AccountSupportContactProps {
  accountId: string;
}

export default function AccountSupportContact({
  accountId,
}: AccountSupportContactProps) {
  const { hasAccess } = usePermissions();
  const {
    data: accountData,
    isLoading,
    isFetching,
  } = useGetAccountDataQuery(accountId);
  const [updateSupportEmail, updatingSupportEmail] =
    useUpdateAccountSupportEmailMutation();
  const [hasChanges, setHasChanges] = useState(false);
  const [supportEmail, setSupportEmail] = useState('');
  const [isValidEmail, setIsValidEmail] = useState(true);
  const [isValidDomainEmail, setIsValidDomainEmail] = useState(true);
  const permissionCheck = (): boolean => {
    if (
      hasAccess({
        permissions: [PERMISSIONS.ManageElevatedAdmins],
      })
    ) {
      return true;
    }
    if (
      hasAccess({
        permissions: [PERMISSIONS.InviteAdminOrReporter],
      })
    ) {
      return true;
    }
    return false;
  };
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasChanges(accountData?.accountContactEmail !== supportEmail);
    setSupportEmail(e.target.value);
  };

  const onCancel = () => {
    setSupportEmail(accountData?.accountContactEmail ?? '');
    if (accountData?.accountContactEmail?.length === 0) {
      const input: HTMLInputElement = document.querySelector(
        '#accountContactEmailContainer input',
      );
      if (input) {
        setTimeout(() => {
          input.value = '';
          setSupportEmail('');
        }, 1);
      }
    }
  };

  const onSaveButtonClick = () => {
    const normalized = normalizeEmail(supportEmail);
    const isDomainValid =
      normalized.length > 0
        ? !pmiEmailRegex.test(normalized.toLowerCase())
        : false;
    setIsValidDomainEmail(isDomainValid);
    if (!hasChanges || !isValidEmail || !isDomainValid) {
      return;
    }
    updateSupportEmail({ accountId, email: normalized })
      .unwrap()
      .then(() => {
        toastWithAnalytics(
          'Account support email has been updated.',
          'success',
        );
        setHasChanges(false);
      })
      .catch(() =>
        toastWithAnalytics(
          'Error! Account support email was not updated.',
          'danger',
        ),
      );
  };

  const getErrorMessage = () => {
    if (!isValidEmail) {
      return 'Invalid email address';
    }

    if (!isValidDomainEmail) {
      return 'PMI email addresses are prohibited, please enter an email address your company has ownership of and will be available to answer inquiries by your users.';
    }

    return '';
  };

  useEffect(() => {
    if (
      !isLoading &&
      !isFetching &&
      accountData &&
      accountData.accountContactEmail
    ) {
      setSupportEmail(accountData.accountContactEmail);
    }
  }, [isLoading, isFetching]);

  useEffect(() => {
    setHasChanges(accountData?.accountContactEmail !== supportEmail);
    const normalized = normalizeEmail(supportEmail);
    setIsValidEmail(
      normalized.length > 0 ? singleEmailRegex.test(normalized) : true,
    );
    setIsValidDomainEmail(true);
  }, [supportEmail]);

  return permissionCheck() ? (
    <div className="col-12">
      <Card className="p-0">
        <div className="p-4">
          <div className="mb-3">
            <div className="d-flex flex-column flex-sm-row">
              <div className="d-block flex-grow-1">
                <h4 className="fw-bold mb-0">Account Support Contact</h4>
                <p className="mb-4">
                  If users experience issues or need help, provide an email
                  address you want us to display for them to request assistance.
                </p>
              </div>
              <div className="d-sm-block d-none">
                <div className={styles.buttonsBlock}>
                  <LinkButton
                    titleText="Cancel Changes"
                    variant="light"
                    disabled={!hasChanges || updatingSupportEmail.isLoading}
                    onClick={onCancel}
                    className={`${
                      !hasChanges && !updatingSupportEmail.isLoading
                        ? 'invisible'
                        : 'me-2'
                    } ${styles.btnCancel}`}
                  />
                  <Button
                    titleText="Save Changes"
                    variant="primary"
                    disabled={
                      !hasChanges ||
                      updatingSupportEmail.isLoading ||
                      !isValidEmail
                    }
                    onClick={onSaveButtonClick}
                    className={
                      !hasChanges && !updatingSupportEmail.isLoading
                        ? 'invisible'
                        : ''
                    }
                  />
                </div>
              </div>
            </div>
            <div className="d-block">
              <div
                className="d-block d-sm-inline-block"
                id="accountContactEmailContainer"
              >
                <InputField
                  type="text"
                  label="Email"
                  value={supportEmail}
                  id="supportEmailField"
                  className={`mb-2 ${!isValidEmail ? 'is-invalid' : ''} 
                  ${styles.inputField}`}
                  onChange={onChange}
                  isInvalid={!isValidEmail || !isValidDomainEmail}
                  errorMsg={getErrorMessage()}
                  disabled={updatingSupportEmail.isLoading}
                  placeholder="example@mail.com"
                  spellCheck={false}
                />
                <span className="d-block smallI  text-muted">
                  e.g. example@mail.com
                </span>
              </div>
            </div>
            <div className="d-sm-none d-block mt-3">
              <div className={styles.buttonsBlock}>
                <LinkButton
                  titleText="Cancel Changes"
                  variant="light"
                  disabled={!hasChanges || updatingSupportEmail.isLoading}
                  onClick={onCancel}
                  className={`${
                    !hasChanges && !updatingSupportEmail.isLoading
                      ? 'invisible'
                      : 'mx-1'
                  } ${styles.btnCancel}`}
                />
                <Button
                  titleText="Save Changes"
                  variant="primary"
                  disabled={
                    !hasChanges ||
                    updatingSupportEmail.isLoading ||
                    !isValidEmail
                  }
                  onClick={onSaveButtonClick}
                  className={
                    !hasChanges && !updatingSupportEmail.isLoading
                      ? 'invisible'
                      : 'mx-1'
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  ) : null;
}
